import { createChapterBeat, deleteChapterBeat, getChapterBeats, updateChapterBeat, updateChapterBeatsOrder, ChapterBeat } from '@/services/supabase/chapter-beats';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const chapterBeatKeys = {
    all: ['chapter-beats'] as const,
    list: (chapterId: string | number) => [...chapterBeatKeys.all, 'list', chapterId.toString()] as const,
};

export function useChapterBeats(chapterId: string | number | undefined) {
    return useQuery({
        queryKey: chapterBeatKeys.list(chapterId || ''),
        queryFn: async () => {
            if (!chapterId) return [];
            return await getChapterBeats(Number(chapterId));
        },
        enabled: !!chapterId,
    });
}

export function useCreateChapterBeat() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ chapterId, title, description }: { chapterId: number; title: string; description?: string }) => {
            return await createChapterBeat(chapterId, title, description);
        },
        onMutate: async ({ chapterId, title, description }) => {
            await queryClient.cancelQueries({ queryKey: chapterBeatKeys.list(chapterId) });
            const previousBeats = queryClient.getQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId));

            if (previousBeats) {
                // Optimistically add a placeholder beat
                const optimisticBeat: ChapterBeat = {
                    id: Math.random(), // Temp ID
                    chapter_id: chapterId,
                    tenant_id: '',
                    title: title,
                    description: description || '',
                    display_order: previousBeats.length + 1,
                    is_completed: false,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                };
                queryClient.setQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId), [...previousBeats, optimisticBeat]);
            }

            return { previousBeats, chapterId };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousBeats) {
                queryClient.setQueryData(chapterBeatKeys.list(context.chapterId), context.previousBeats);
            }
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: chapterBeatKeys.list(variables.chapterId) });
        },
    });
}

export function useUpdateChapterBeat() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ beatId, updates }: { beatId: number; updates: Partial<ChapterBeat>; chapterId: number }) => {
            return await updateChapterBeat(beatId, updates);
        },
        onMutate: async ({ beatId, updates, chapterId }) => {
            await queryClient.cancelQueries({ queryKey: chapterBeatKeys.list(chapterId) });
            const previousBeats = queryClient.getQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId));

            if (previousBeats) {
                queryClient.setQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId), (old) =>
                    old?.map(beat => beat.id === beatId ? { ...beat, ...updates } : beat)
                );
            }

            return { previousBeats, chapterId };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousBeats) {
                queryClient.setQueryData(chapterBeatKeys.list(context.chapterId), context.previousBeats);
            }
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: chapterBeatKeys.list(variables.chapterId) });
        },
    });
}

export function useDeleteChapterBeat() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ beatId }: { beatId: number; chapterId: number }) => {
            return await deleteChapterBeat(beatId);
        },
        onMutate: async ({ beatId, chapterId }) => {
            await queryClient.cancelQueries({ queryKey: chapterBeatKeys.list(chapterId) });
            const previousBeats = queryClient.getQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId));

            if (previousBeats) {
                queryClient.setQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId), (old) =>
                    old?.filter(beat => beat.id !== beatId)
                );
            }

            return { previousBeats, chapterId };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousBeats) {
                queryClient.setQueryData(chapterBeatKeys.list(context.chapterId), context.previousBeats);
            }
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: chapterBeatKeys.list(variables.chapterId) });
        },
    });
}

export function useUpdateChapterBeatsOrder() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ beats }: { beats: { id: number; display_order: number }[]; chapterId: number }) => {
            return await updateChapterBeatsOrder(beats);
        },
        onMutate: async ({ beats, chapterId }) => {
            await queryClient.cancelQueries({ queryKey: chapterBeatKeys.list(chapterId) });
            const previousBeats = queryClient.getQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId));

            if (previousBeats) {
                const newBeats = [...previousBeats].sort((a, b) => {
                    const orderA = beats.find(b => b.id === a.id)?.display_order ?? a.display_order;
                    const orderB = beats.find(b => b.id === b.id)?.display_order ?? b.display_order;
                    return orderA - orderB;
                });
                queryClient.setQueryData<ChapterBeat[]>(chapterBeatKeys.list(chapterId), newBeats);
            }

            return { previousBeats, chapterId };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousBeats) {
                queryClient.setQueryData(chapterBeatKeys.list(context.chapterId), context.previousBeats);
            }
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: chapterBeatKeys.list(variables.chapterId) });
        },
    });
}
